import { supabase } from '@/core/supabase/client';
import type { MobilityMode } from '@/features/clients/clients';
import type { Calculation } from '@/features/catalog/catalog';
import type { SplitLine } from '@/core/economics';

export interface Participant {
  id: string;
  display_name: string;
  kind: 'person' | 'entity' | 'org_account';
  is_active: boolean;
  /**
   * La membresía de la persona que ENTRA a la aplicación, cuando este
   * participante es además un usuario. `null` = cobra pero no entra, que es el
   * caso normal de un socio que no usa el programa.
   *
   * De esto depende que esa persona vea sus propios números: `my_participant_id`
   * lo resuelve por acá, y sin el vínculo RLS le oculta hasta sus propias
   * líneas.
   */
  member_id: string | null;
  /**
   * Color con el que se lo reconoce en el listado de trabajos.
   *
   * Representa **a quién está atribuida la facturación** del trabajo, y nada
   * más: no el reparto de ganancia, no la movilidad, no el destinatario externo
   * de la factura. Agosto mostró que las cuatro pueden diferir en un mismo
   * trabajo. `null` = sin color.
   */
  color?: string | null;
}

export interface WorkDefaults {
  organization_id: string;
  km: number | null;
  charge_mobility: boolean | null;
  mobility_mode: MobilityMode | null;
  mobility_rate_cents: number | null;
  flat_mobility_cents: number | null;
  mobility_participant_id: string | null;
  biller_participant_id: string | null;
  split_template_id: string | null;
}

/** Una línea de honorario del formulario. Los kg son EDITABLES por trabajo. */
export interface WorkItemDraft {
  key: string;
  organization_service_id: string | null;
  service_name: string;
  calculation: Calculation;
  /** kg sugeridos por el tarifario, para poder avisar si se modificaron. */
  kg_suggested: number;
  /**
   * `null` mientras el campo está vacío.
   *
   * Vacío no es cero: "todavía no lo escribí" y "vale cero" son cosas
   * distintas, y confundirlas obligaba a borrar un `0` antes de poder tipear.
   * Al guardar se resuelve como 0.
   */
  kg_per_unit: number | null;
  quantity: number | null;
}

export interface SplitDraft {
  participant_id: string;
  percentage: number;
  sort_order: number;
}

export interface MobilityDraft {
  mode: MobilityMode;
  km: number | null;
  rate_cents: number | null;
  amount_cents: number | null;
  allocations: SplitDraft[];
}

export interface WorkPayload {
  organization_id: string;
  client_id: string;
  establishment_id: string | null;
  work_date: string;
  billing_year: number;
  billing_month: number;
  biller_participant_id: string;
  description: string | null;
  is_billed: boolean;
  is_paid: boolean;
  items: {
    organization_service_id: string | null;
    service_name: string;
    calculation: Calculation;
    kg_per_unit: number;
    quantity: number;
    sort_order: number;
  }[];
  splits: SplitDraft[];
  mobility: {
    mode: MobilityMode;
    km: number | null;
    rate_cents: number | null;
    amount_cents: number | null;
    allocations: SplitDraft[];
  } | null; /**
   * A nombre de quién se emite la factura, congelado en el trabajo.
   *
   * Viaja DENTRO del payload y no en una llamada aparte porque un trabajo
   * cargado sin señal se sincroniza más tarde: si el reparto se leyera al
   * sincronizar, un cambio en el cliente hecho mientras tanto reescribiría a
   * quién había que facturarle. Un snapshot que se toma después no es un
   * snapshot.
   */
  invoice_recipients?: { name: string; percentage: number; sort_order: number }[];
}

/** Participantes que pueden facturar o recibir reparto. Excluye la cuenta técnica. */
export async function listParticipants(organizationId: string): Promise<Participant[]> {
  const { data, error } = await supabase
    .from('participants')
    .select('id, display_name, kind, is_active, color, member_id')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .neq('kind', 'org_account')
    .order('display_name');
  if (error) throw error;
  return (data ?? []) as Participant[];
}

/**
 * TODOS los participantes, incluida la cuenta técnica de la organización.
 *
 * `listParticipants` la excluye a propósito: no es una persona y no tiene que
 * aparecer en ningún desplegable donde se elige a quién facturarle o a quién
 * repartirle. Pero SÍ aparece en la liquidación —ahí va a parar lo que no tiene
 * facturador asignado, para que las cuentas cierren en cero—, y ahí hace falta
 * poder reconocerla para decir que no es un socio más.
 *
 * Sin esto, la pantalla que reparte la plata mostraba a la veterinaria como si
 * fuera una persona.
 */
export async function listAllParticipants(organizationId: string): Promise<Participant[]> {
  const { data, error } = await supabase
    .from('participants')
    .select('id, display_name, kind, is_active, color, member_id')
    .eq('organization_id', organizationId)
    .order('display_name');
  if (error) throw error;
  return (data ?? []) as Participant[];
}

/** Defaults heredados: Organización → Cliente → Establecimiento. */
export async function fetchWorkDefaults(
  clientId: string,
  establishmentId: string | null,
): Promise<WorkDefaults> {
  const { data, error } = await supabase.rpc('work_defaults', {
    p_client: clientId,
    p_establishment: establishmentId,
  });
  if (error) throw error;
  return data as WorkDefaults;
}

/**
 * Alta ATÓMICA del trabajo.
 * Una sola transacción: el trabajo entra completo o no entra (ver migración 13).
 */
export async function createWork(payload: WorkPayload): Promise<string> {
  const { data, error } = await supabase.rpc('create_work_session', { p: payload });
  if (error) throw error;
  return data as string;
}

export async function updateWork(workId: string, payload: WorkPayload): Promise<void> {
  const { error } = await supabase.rpc('update_work_session', { p_work: workId, p: payload });
  if (error) throw error;
}

export interface WorkAmountsRow {
  work_session_id: string;
  total_kg: number;
  honoraria_cents: number | null;
  mobility_cents: number | null;
  total_cents: number | null;
  price_pending: boolean;
}

export async function fetchWorkAmounts(workId: string): Promise<WorkAmountsRow | null> {
  const { data, error } = await supabase
    .from('work_amounts')
    .select('*')
    .eq('work_session_id', workId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as WorkAmountsRow | null;
}

// ---------------------------------------------------------------------------
// Listado (tarea 6.1)
// ---------------------------------------------------------------------------
// Lee la vista `work_list`, que ya trae el nombre del cliente, del campo, del
// facturador, los importes y el resumen de honorarios. Una sola consulta en
// lugar de una cascada de joins desde el cliente.

export interface WorkListRow {
  id: string;
  organization_id: string;
  work_date: string;
  billing_year: number;
  billing_month: number;
  client_id: string;
  client_name: string;
  establishment_id: string | null;
  establishment_name: string | null;
  biller_participant_id: string;
  biller_name: string;
  created_by: string | null;
  created_by_name: string | null;
  description: string | null;
  is_billed: boolean;
  billed_at: string | null;
  is_paid: boolean;
  paid_at: string | null;
  is_locked: boolean;
  price_frozen: boolean;
  closure_id: string | null;
  total_kg: number | null;
  honoraria_cents: number | null;
  mobility_cents: number | null;
  total_cents: number | null;
  price_pending: boolean;
  service_ids: string[];
  service_names: string;
  item_count: number;
}

export interface WorkFilters {
  year?: number | null | undefined;
  month?: number | null | undefined;
  clientId?: string | null | undefined;
  establishmentId?: string | null | undefined;
  createdBy?: string | null | undefined;
  serviceId?: string | null | undefined;
  /** `null` = sin filtrar. `true`/`false` = filtrar por ese estado. */
  isBilled?: boolean | null | undefined;
  isPaid?: boolean | null | undefined;
}

export const FILTROS_VACIOS: WorkFilters = {
  year: null,
  month: null,
  clientId: null,
  establishmentId: null,
  createdBy: null,
  serviceId: null,
  isBilled: null,
  isPaid: null,
};

export function hayFiltrosActivos(f: WorkFilters): boolean {
  return (Object.keys(FILTROS_VACIOS) as (keyof WorkFilters)[]).some(
    (k) => f[k] !== null && f[k] !== undefined,
  );
}

export async function listWorks(
  organizationId: string,
  filters: WorkFilters = {},
  limit = 100,
): Promise<WorkListRow[]> {
  let q = supabase
    .from('work_list')
    .select('*')
    .eq('organization_id', organizationId)
    .order('work_date', { ascending: false })
    .order('billing_year', { ascending: false })
    .order('billing_month', { ascending: false })
    .limit(limit);

  if (filters.year != null) q = q.eq('billing_year', filters.year);
  if (filters.month != null) q = q.eq('billing_month', filters.month);
  if (filters.clientId) q = q.eq('client_id', filters.clientId);
  if (filters.establishmentId) q = q.eq('establishment_id', filters.establishmentId);
  if (filters.createdBy) q = q.eq('created_by', filters.createdBy);
  // `contains` sobre el arreglo de ids de honorario: filtro por TIPO de trabajo.
  if (filters.serviceId) q = q.contains('service_ids', [filters.serviceId]);
  if (filters.isBilled != null) q = q.eq('is_billed', filters.isBilled);
  if (filters.isPaid != null) q = q.eq('is_paid', filters.isPaid);

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as WorkListRow[];
}

/** Vistas rápidas de la tarea 6.4. */
export const PENDIENTE_DE_FACTURAR: WorkFilters = { ...FILTROS_VACIOS, isBilled: false };
export const PENDIENTE_DE_COBRAR: WorkFilters = {
  ...FILTROS_VACIOS,
  isBilled: true,
  isPaid: false,
};

/**
 * Lo que ya está cobrado.
 *
 * Ahora que liquidar el mes no congela nada, la lista de trabajos es el lugar
 * donde se ve cuánto falta para que el mes esté realmente cerrado: los cobrados
 * son los que ya no esperan nada.
 */
export const YA_COBRADO: WorkFilters = {
  ...FILTROS_VACIOS,
  isPaid: true,
};

// ---------------------------------------------------------------------------
// Estados rápidos (tareas 6.2, 6.3, 6.5)
// ---------------------------------------------------------------------------
// Van por RPC dedicada: escribe sólo las cuatro columnas de estado, así el
// gesto rápido no puede tocar honorarios, reparto ni movilidad.

export interface CambioDeEstado {
  solicitados: number;
  modificados: number;
  sin_cambios: number;
}

export async function setWorkStatus(
  workId: string,
  cambio: { isBilled?: boolean | null; isPaid?: boolean | null },
): Promise<CambioDeEstado> {
  const { data, error } = await supabase.rpc('set_work_status', {
    p_work: workId,
    p_is_billed: cambio.isBilled ?? null,
    p_is_paid: cambio.isPaid ?? null,
  });
  if (error) throw error;
  return data as CambioDeEstado;
}

export async function setWorksStatus(
  workIds: readonly string[],
  cambio: { isBilled?: boolean | null; isPaid?: boolean | null },
): Promise<CambioDeEstado> {
  const { data, error } = await supabase.rpc('set_works_status', {
    p_works: workIds,
    p_is_billed: cambio.isBilled ?? null,
    p_is_paid: cambio.isPaid ?? null,
  });
  if (error) throw error;
  return data as CambioDeEstado;
}

// ---------------------------------------------------------------------------
// Carga de un trabajo para editar o duplicar
// ---------------------------------------------------------------------------

export interface WorkItemLoaded {
  organization_service_id: string | null;
  service_name: string;
  calculation: Calculation;
  /** kg guardados en el trabajo (snapshot). */
  kg_per_unit: number;
  /** kg que hoy tiene el tarifario. `null` si el honorario ya no existe. */
  kg_current: number | null;
  quantity: number;
  sort_order: number;
}

export interface WorkLoaded {
  id: string;
  organization_id: string;
  client_id: string;
  client_name: string;
  establishment_id: string | null;
  establishment_name: string | null;
  work_date: string;
  billing_year: number;
  billing_month: number;
  biller_participant_id: string;
  description: string | null;
  is_billed: boolean;
  is_paid: boolean;
  is_locked: boolean;
  price_frozen: boolean;
  steer_price_cents: number | null;
  fuel_price_cents: number | null;
  items: WorkItemLoaded[];
  splits: SplitDraft[];
  mobility: MobilityDraft | null;
}

export async function fetchWorkForEdit(workId: string): Promise<WorkLoaded> {
  const { data, error } = await supabase.rpc('work_for_edit', { p_work: workId });
  if (error) throw error;
  return data as WorkLoaded;
}

// ---------------------------------------------------------------------------
// Plantillas de reparto (acción rápida del formulario)
// ---------------------------------------------------------------------------

export interface SplitTemplate {
  id: string;
  name: string;
  lines: SplitDraft[];
}

export async function listSplitTemplates(organizationId: string): Promise<SplitTemplate[]> {
  const { data, error } = await supabase
    .from('split_templates')
    .select('id, name, lines:split_template_lines(participant_id, percentage, sort_order)')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return (data ?? []).map((t) => {
    const row = t as { id: string; name: string; lines: SplitDraft[] | null };
    return {
      id: row.id,
      name: row.name,
      lines: [...(row.lines ?? [])].sort((a, b) => a.sort_order - b.sort_order),
    };
  });
}

// ---------------------------------------------------------------------------
// Conversión entre el borrador del formulario y el motor económico
// ---------------------------------------------------------------------------
// El payload de la base usa snake_case; el motor usa camelCase. La conversión
// vive acá para que el formulario no la repita.

export function toSplitLines(drafts: readonly SplitDraft[]): SplitLine[] {
  return drafts.map((d) => ({
    participantId: d.participant_id,
    percentage: d.percentage,
    sortOrder: d.sort_order,
  }));
}

export function fromSplitLines(lines: readonly SplitLine[]): SplitDraft[] {
  return lines.map((l) => ({
    participant_id: l.participantId,
    percentage: l.percentage,
    sort_order: l.sortOrder,
  }));
}

// ---------------------------------------------------------------------------
// Períodos cerrados
// ---------------------------------------------------------------------------

/**
 * Un período cerrado no admite trabajos nuevos (migración 17). Se consulta
 * ANTES de guardar sólo para avisar a tiempo: quien rechaza de verdad es el
 * trigger de la base, que cubre por igual el alta, el duplicado y el intento
 * de mover un trabajo hacia un mes ya cerrado.
 */
export async function periodIsClosed(
  organizationId: string,
  year: number,
  month: number,
): Promise<boolean> {
  const { data, error } = await supabase.rpc('period_is_closed', {
    p_org: organizationId,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return Boolean(data);
}

/**
 * Alta, edición y baja de plantillas de reparto.
 *
 * La plantilla y sus líneas son dos tablas, así que guardar es: crear o
 * renombrar la plantilla, borrar las líneas viejas y escribir las nuevas. No es
 * elegante, pero es correcto y no deja líneas huérfanas de un reparto anterior
 * —que aparecerían sumando a un porcentaje que ya nadie eligió—.
 */
export async function saveSplitTemplate(input: {
  organizationId: string;
  id?: string;
  name: string;
  lines: SplitDraft[];
}): Promise<string> {
  let id = input.id;

  if (id) {
    const { error } = await supabase
      .from('split_templates')
      .update({ name: input.name })
      .eq('id', id);
    if (error) throw error;
    const { error: e2 } = await supabase
      .from('split_template_lines')
      .delete()
      .eq('template_id', id);
    if (e2) throw e2;
  } else {
    const { data, error } = await supabase
      .from('split_templates')
      .insert({ organization_id: input.organizationId, name: input.name })
      .select('id')
      .single();
    if (error) throw error;
    id = (data as { id: string }).id;
  }

  const { error } = await supabase.from('split_template_lines').insert(
    input.lines.map((l) => ({
      organization_id: input.organizationId,
      template_id: id,
      participant_id: l.participant_id,
      percentage: l.percentage,
      sort_order: l.sort_order,
    })),
  );
  if (error) throw error;
  return id;
}

/**
 * Se desactiva, no se borra: un trabajo viejo puede haberse cargado con ella y
 * el historial tiene que poder explicarse.
 */
export async function deactivateSplitTemplate(id: string): Promise<void> {
  const { error } = await supabase
    .from('split_templates')
    .update({ is_active: false })
    .eq('id', id);
  if (error) throw error;
}

/** La plantilla habitual de la organización. `null` la quita. */
export async function setOrganizationDefaultTemplate(
  organizationId: string,
  templateId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('organization_settings')
    .update({ default_split_template_id: templateId })
    .eq('organization_id', organizationId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Eliminar y anular
// ---------------------------------------------------------------------------

/**
 * ¿Este trabajo se puede borrar?
 *
 * Se puede casi siempre, y ése fue el cambio: antes se negaba en cuanto el
 * trabajo hubiera tocado plata, con el argumento de que borrarlo dejaría la
 * liquidación de ese mes imposible de reconstruir. Era falso. La foto del
 * cierre guarda su propia copia congelada de todo, y ninguna consulta del
 * cierre pasa por el trabajo vivo.
 *
 * Queda una sola condición previa, y es un camino y no una pared: mientras el
 * período está CERRADO sus números están firmados. Se reabre, se corrige, se
 * vuelve a cerrar.
 *
 * Esto decide qué botón mostrar. Lo que decide si la operación se hace es la
 * base. Ocultar un botón es ergonomía, no seguridad.
 */
export function sePuedeEliminar(w: { is_billed?: boolean }): boolean {
  return !w.is_billed;
}

/**
 * ¿Se puede tocar la economía de este trabajo?
 *
 * El punto de no retorno es la FACTURA, no el cierre del mes. Entre liquidar
 * agosto y terminar de facturarlo pasan días, y en esos días aparecen las
 * correcciones: una cantidad mal cargada, un kilometraje equivocado. Un trabajo
 * que todavía no se facturó se corrige aunque su mes ya esté liquidado.
 *
 * Esto decide qué se muestra. Lo que decide si la operación se hace es la base:
 * el invariante rechaza cualquier cambio económico sobre un trabajo facturado,
 * venga de donde venga.
 */
export function sePuedeEditar(w: { is_billed?: boolean }): boolean {
  return !w.is_billed;
}

/**
 * Deshacer una facturación.
 *
 * No es un cambio de estado más: deshace algo que ya salió. Por eso no pasa por
 * `setWorkStatus` —que lo rechaza— sino por su propia función, que exige
 * administrador y deja el rastro en `billing_reversals`.
 *
 * El motivo es OPCIONAL. Exigirlo sólo lograba que se escribiera cualquier cosa
 * para poder seguir; lo que hace falta para reconstruir qué pasó es quién lo
 * revirtió y cuándo, y eso se registra solo.
 */
export async function revertirFacturacion(workId: string, motivo?: string): Promise<void> {
  const { error } = await supabase.rpc('revertir_facturacion', {
    p_work: workId,
    p_motivo: motivo?.trim() || null,
  });
  if (error) throw error;
}

/**
 * ¿Y anular? Cuando el trabajo YA movió plata.
 *
 * Son dos intenciones distintas, no dos niveles de permiso. Anular es «esto no
 * va, pero quiero que quede por qué»: sale de las cuentas y deja el motivo.
 * Borrar es «esto nunca debió existir». Un trabajo que todavía no tocó nada no
 * necesita explicación; uno que sí, casi siempre la merece, así que se ofrece
 * primero.
 */
export function convieneAnular(w: { is_billed: boolean; closure_id?: string | null }): boolean {
  // Un trabajo facturado no se anula: primero se revierte la facturación. Lo
  // que queda para anular es el que ya entró en una liquidación pero todavía no
  // se facturó: sale de las cuentas y queda el motivo.
  return !w.is_billed && !!w.closure_id;
}

export async function deleteWork(workId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_work_session', { p_work: workId });
  if (error) throw error;
}

export async function voidWork(workId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('void_work_session', {
    p_work: workId,
    p_reason: reason,
  });
  if (error) throw error;
}
